/**
 * AnalyticsService - Singleton service that buffers events, batches, and sends to the analytics ingest API.
 * See app_notes/ANALYTICS_SERVICE_IMPLEMENTATION_PLAN.md for schema and event catalog.
 */

import axios from 'axios';
import { createRequest, BASE_URL } from './httpClient';
import SessionService from './sessionService';

const FLUSH_THRESHOLD = 10;
const FLUSH_INTERVAL_MS = 30 * 1000;
const MAX_BATCH_SIZE = 50;

class AnalyticsService {
    static _instance = null;

    constructor() {
        if (AnalyticsService._instance) {
            return AnalyticsService._instance;
        }
        this.buffer = [];
        this.viewContext = { viewName: null, prevView: null };
        this.flushTimer = null;
        this._setupVisibilityFlush();
        AnalyticsService._instance = this;
    }

    static getInstance() {
        if (!AnalyticsService._instance) {
            AnalyticsService._instance = new AnalyticsService();
        }
        return AnalyticsService._instance;
    }

    /**
     * Get the analytics ingest endpoint URL.
     * @returns {string|null} Endpoint URL or null if not configured
     */
    _getEndpoint() {
        const override = process.env.REACT_APP_ANALYTICS_API_URL;
        if (override) return override;
        if (BASE_URL) return `${BASE_URL}/analytics/events`;
        return null;
    }

    /**
     * Set current view context so it is attached to subsequent events.
     * @param {string} viewName - Current app view (e.g. feed, snipedOdds, parlay)
     * @param {string} [prevView] - Previous view, if relevant
     */
    setViewContext(viewName, prevView) {
        this.viewContext = {
            viewName: viewName || null,
            prevView: prevView !== undefined ? prevView : this.viewContext.prevView
        };
    }

    /**
     * Build a single event object with timestamp, context, and optional properties.
     * @param {string} eventName - Event type (use analyticsConstants)
     * @param {Object} [properties] - Flat key-value event-specific data
     * @returns {Object} Event object for the batch
     */
    _buildEvent(eventName, properties = {}) {
        const session = SessionService.getInstance();
        const event = {
            eventName,
            timestamp: new Date().toISOString(),
            eventId: crypto.randomUUID(),
            sessionId: session.getSessionId() || undefined,
            userId: session.getUserId() || undefined,
            viewName: this.viewContext.viewName || undefined,
            prevView: this.viewContext.prevView || undefined,
            isAuthenticated: session.isAuthenticated()
        };
        if (Object.keys(properties).length > 0) {
            event.properties = properties;
        }
        return event;
    }

    /**
     * Flush buffer to the API (send batch). Clears sent events from buffer.
     * Non-blocking; errors are logged and not thrown.
     */
    async _flush() {
        if (this.buffer.length === 0) return;
        const endpoint = this._getEndpoint();
        if (!endpoint) {
            if (process.env.NODE_ENV === 'development') {
                console.log('[Analytics] No endpoint configured; dropping', this.buffer.length, 'events');
            }
            this.buffer = [];
            return;
        }
        const batch = this.buffer.splice(0, MAX_BATCH_SIZE);
        const body = createRequest({ events: batch });
        try {
            await axios.post(endpoint, body, {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true
            });
        } catch (err) {
            console.error('[Analytics] Failed to send events:', err?.message || err);
        }
    }

    /**
     * Schedule a flush after FLUSH_INTERVAL_MS if not already scheduled.
     */
    _scheduleFlush() {
        if (this.flushTimer) return;
        this.flushTimer = setTimeout(() => {
            this.flushTimer = null;
            this._flush();
        }, FLUSH_INTERVAL_MS);
    }

    /**
     * Register visibility/pagehide listeners to flush on tab close or hide.
     */
    _setupVisibilityFlush() {
        if (typeof document === 'undefined') return;
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') this._flush();
        };
        const onPageHide = () => this._flush();
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', onPageHide);
    }

    /**
     * Track an analytics event. Buffers the event and flushes when threshold or timer is met.
     * @param {string} eventName - Event type (use constants from utils/analyticsConstants.js)
     * @param {Object} [properties] - Flat key-value event-specific data (no nested objects)
     */
    track(eventName, properties = {}) {
        if (!eventName || typeof eventName !== 'string') return;
        const event = this._buildEvent(eventName, properties);
        this.buffer.push(event);
        if (this.buffer.length >= FLUSH_THRESHOLD) {
            this._flush();
        } else {
            this._scheduleFlush();
        }
    }
}

export default AnalyticsService;
